import {
  ProjectDescription,
  ProjectStatusDto,
} from '../../pages/webapp/projects/projects.interface';

// WARNING: enum values are used in the api call, do not change them without justification
export enum ROLLUP_VIEW_TYPES {
  // manager
  PROJECTS = 'project_list', // this does not exist backend
  BUDGET_LINE_ITEMS = 'budget_line',
  TAGS = 'budget_tag',

  // paid service provider
  SUB_PROJECT = 'sub_project',
  PRIME_PROJECT = 'prime_project',

  // free service provider
  SP_PROJECTS = 'sp_project_list', // this does not exist backend
}

export interface IProjectTags {
  capex: number;
  opex: number;
  priority: number;
  approved: number;
}

export interface IRollupProject {
  id: number;
  title: string;
  spend_category: number;
  project_status: ProjectStatusDto;
  priority: boolean;
  type: number;
  project_type: IProjectType;
  approved: boolean;
  property_id: number;
  start_date: string;
  project_property: IProjectProperty;
  cash_flow: ICashFlow;
  budget_tags: IBudgetTag;
  work_percentage: number;
  days_due: number;
  tags: string[];
  description: Partial<ProjectDescription>;
  project_id: string;
}

export interface IRollupPrimeProject {
  id: number;
  project_id: string;
  title: string;
  project_status: ProjectStatusDto;
  short_description: string;
  current_budget: number;
  prime_contract: number;
  anticipated_income: number;
  committed_costs?: number;
  anticipated_costs?: number;
  anticipated_fee: number;
  over_under: number;
  under_over: number;
  progress: RollupPrimeProjectProgress;
}
export interface RollupPrimeProjectProgress {
  work: {
    done: number;
  };
  billed: {
    invoiced: number;
    paid: number;
  };
  paid: {
    invoiced: number;
    paid: number;
  };
}

export interface IRollupSubProject {
  id: number;
  project_id: string;
  title: string;
  project_status: ProjectStatusDto;
  short_description: string;
  original_subcontract: number;
  approved_scos: number;
  pending_scos: number;
  current_subcontract: number;
  total_invoiced: number;
  paid_invoices: number;
  left_to_invoice: number;
  progress: RollupSubProjectProgress;
}

export interface RollupSubProjectProgress {
  billed: {
    invoiced: number;
    paid: number;
  };
  paid: {
    invoiced: number;
    paid: number;
  };
}

export interface IRollupProjectTable {
  id: number;
  title: string;
  spend_category: number;
  project_status: ProjectStatusDto;
  start_date: string;
  priority: boolean;
  type: number;
  project_type: IProjectType;
  approved: boolean;
  property_id: number;
  project_property: IProjectProperty;
  current_budget: number; // ICashFlow
  contracts: number; // ICashFlow
  total_committed: number; // ICashFlow
  actuals: number; // ICashFlow
  forecasts_to_complete: number; // ICashFlow
  forecast_modifications: number; // ICashFlow
  total_invoiced: number; // ICashFlow
  // total_committed_vs_current_budget: number;
  budget_tags: IBudgetTag;
  complete: number;
  work_percentage: number;
  days_due: number;
  tags: string[];
  description: Partial<ProjectDescription>;
  project_id: string;
}

export interface IRollupProjectGCTable extends IRollupProjectTable {
  prime_contract: number;
  anticipated_income: number;
  over_under: number; // todo: see if forecasts_to_complete makes it or use this field.. // “Prime Contract” + “Anticipated Income”) - “Current Budget”. Tooltop: “Difference between income and budget. “Over” means that there is enough income to cover the cost of the project.”
  under_over: number; // Under/(Over): column is a calculation “Current Budget” - (“Committed Costs” + “Anticipated Costs”). Tooltop: “Difference between project budget and commitments. “Under” means that the costs on the project are covered by available funds.”
  anticipated_fee: number;
  anticipated_costs: number;
  billed_progress: number;
}

export interface IRollupProjectTableTotals {
  title?: string;
  total_projects?: string;
  current_budget: number;
  contracts: number;
  total_committed: number;
  actuals: number;
  forecasts_to_complete: number;
  forecast_modifications: number;
  total_invoiced: number;
  total_committed_vs_current_budget: number;
  complete: number;
}

export interface IRollupProjectTableGCTotals {
  title?: string;
  current_budget: number;
  prime_contract: number;
  anticipated_income: number;
  over_under: number;
  committed_costs: number;
  anticipated_costs: number;
  under_over: number;
  anticipated_fee: number;
}

export interface IRollupLineItemTableTotals {
  title?: string;
  current_budget: number;
  total_committed: number;
  actuals: number;
  complete: number;
  total_committed_vs_current_budget: number;
}

export interface IRollupPrimeProjectsTotals extends Partial<IRollupPrimeProject> {
  total_projects?: string;
}

export interface IRollupSubProjectsTotals extends Partial<IRollupSubProject> {
  total_projects?: string;
}

export interface IRollupLineGCItemTableTotals extends IRollupLineItemTableTotals {
  anticipated_income: number;
  anticipated_costs: number;
  anticipated_fee: number;
  forecasts_to_complete: number;
  billed_progress: number;
  under_over: number;
}

export interface IRollupTagTotals {
  title?: string;
  total_committed: number;
  actuals: number;
  complete: number;
  actuals_vs_total_committed: number;
}

export interface IProjectType {
  id: number;
  name: string;
}

export interface IProjectProperty {
  id: number;
  user_id: number;
  type_unit_value: number;
  type_id: number;
  name: string;
  tax_id: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  status: number;
  created_at: string;
  updated_at: string;
  deleted_at: any;
  formatted_address: string;
}

export interface ICashFlow {
  current_budget: number;
  contracts: number;
  total_committed: number;
  actuals: number;
  total_invoiced: number;
  forecast_modifications: number;
  forecasts_to_complete: number;
}

export interface IBudgetTag {
  capex: boolean;
  opex: boolean;
  priority: boolean;
  approved: boolean;
}

export interface IRollupLineItems {
  name: string;
  projects: IRollupLineItemProject[];
  cash_flow: IRollupGeneralCashFlow;
}

export interface IRollupGCLineItem {
  name: string;
  template_id: number;
  cost_type: string;
  division: string;
  current_budget: number;
  prime_contract: number;
  anticipated_income: number;
  over_under: number;
  committed_costs: number;
  anticipated_costs: number;
  under_over: number;
  anticipated_fee: number;
  progress: RollupPrimeProjectProgress;
  projects: IRollupGCLineItemProject[];
}

export interface IRollupGCLineItemProject {
  id: number;
  name: string;
  project_id: number;
  project_title: string;
  current_budget: number;
  prime_contract: number;
  anticipated_income: number;
  over_under: number;
  committed_costs: number;
  anticipated_costs: number;
  under_over: number;
  anticipated_fee: number;
}

export interface IRollupLineItemTable {
  name: string;
  projects: IRollupLineItemProject[];
  current_budget: number;
  actuals: number;
  total_committed: number;
  total_committed_vs_current_budget: number;
  complete: number;
}

export interface IRollupGCLineItemTable extends IRollupLineItemTable {
  prime_contract: number;
  anticipated_income: number;
  anticipated_costs: number;
  anticipated_fee: number;
  forecasts_to_complete: number;
  billed_progress: number;
  under_over: number;
}

export interface IRollupTag {
  name: string;
  items: IRollupTagItems[];
  cash_flow: IRollupTagCashFlow;
}

export interface IRollupTagTable {
  name: string;
  items: IRollupTagItems[];
  total_committed: number;
  actuals: number;
  complete: number;
  actuals_vs_total_committed: number;
}
export interface IRollupTagItems {
  id: number;
  name: string;
  cash_flow: IRollupTagCashFlow;
}

export interface IRollupTagCashFlow {
  total_committed: number;
  actuals: number;
}

export interface IRollupLineItemProject {
  id: number;
  title: string;
  cash_flow: CashFlow;
}

export interface CashFlow {
  total_committed: number;
  actuals: number;
}

export interface IRollupGeneralCashFlow {
  total_committed: number;
  actuals: number;
  current_budget: number;
}

export interface IRollupGCGeneralCashFlow extends IRollupGeneralCashFlow {
  forecasts_to_complete: number;
  prime_contract: number;
  anticipated_income: number;
  under_over: number;
  anticipated_fee: number;
  anticipated_costs: number;
  billed_progress: number;
}

export interface IRollupFilters {
  // we might extend later - these filters are stored, the other ones change
  project_status?: number[];
  property_ids?: number[];
}
